/**
 * API service for order submission and offline sync
 */

import { OrderPayload } from './types';
import { offlineStorage } from './storage';
import type { Product, UIText } from './types';

// Get API settings from WordPress localized data
declare global {
  interface Window {
    sprintRayFormSettings?: {
      apiEndpoint: string;
      productsEndpoint?: string;
      translationsEndpoint?: string;
      eventEndpoint?: string;
      nonce: string;
      ajaxUrl: string;
      eventId?: number | null;
      lang?: string;
      translationOverrides?: Record<string, string>;
    };
  }
}

class APIService {
  private apiEndpoint: string;
  private nonce: string;
  private productsEndpoint: string;
  private translationsEndpoint: string;
  private eventEndpoint: string | null;
  private eventId: number | null;
  private lang: string;
  // Guards against concurrent sync passes — syncPendingOrders() can be
  // triggered redundantly (on mount, on the browser 'online' event, and
  // recursively from within itself), and without this lock two passes
  // could resubmit the same pending order at once.
  private isSyncingPendingOrders = false;

  constructor() {
    const settings = window.sprintRayFormSettings;
    if (!settings) {
      throw new Error('SprintRay form settings not found');
    }
    this.apiEndpoint = settings.apiEndpoint;
    this.productsEndpoint = settings.productsEndpoint || '/wp-json/sprintray/v1/products';
    this.translationsEndpoint = settings.translationsEndpoint || '/wp-json/sprintray/v1/translations/de';
    this.eventEndpoint = settings.eventEndpoint || null;
    this.eventId = settings.eventId || null;
    this.lang = settings.lang || 'de';
    this.nonce = settings.nonce;
  }

  /**
   * Submit order to the REST API
   */
  async submitOrder(payload: OrderPayload): Promise<{ success: boolean; message: string; orderId?: string }> {
    console.log('🚀 [API] Submitting order to WordPress REST API...');
    console.log('🌐 [API] Endpoint:', this.apiEndpoint);
    console.log('📧 [API] Customer email:', payload.customerInfo.email);
    console.log('💰 [API] Total amount:', payload.totalAmount);

    try {
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': this.nonce,
        },
        body: JSON.stringify(payload),
      });

      console.log('📡 [API] Response status:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ [API] Order submitted successfully!', { orderId: data.orderId, message: data.message });
      return {
        success: true,
        message: data.message || 'Order submitted successfully',
        orderId: data.orderId,
      };
    } catch (error) {
      console.error('❌ [API] Error submitting order:', error);
      throw error;
    }
  }

  /**
   * Fetch products from the API
   */
  async fetchProducts(): Promise<{ products: Product[] }> {
    console.log('📦 [API] Fetching products from WordPress REST API...');

    try {
      // Build URL with event_id and lang if available
      let url = this.productsEndpoint;
      const params = new URLSearchParams();

      if (this.eventId) {
        params.append('event_id', String(this.eventId));
      }

      if (this.lang) {
        params.append('lang', this.lang);
      }

      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url, {
        headers: {
          'X-WP-Nonce': this.nonce,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ [API] Products fetched successfully:', data.products?.length || 0);

      return {
        products: data.products || [],
      };
    } catch (error) {
      console.error('❌ [API] Error fetching products:', error);
      // Return empty array on error
      return { products: [] };
    }
  }

  /**
   * Fetch translations from the API
   */
  async fetchTranslations(): Promise<UIText | null> {
    console.log('🌐 [API] Fetching translations from WordPress REST API...');

    try {
      const response = await fetch(this.translationsEndpoint, {
        headers: {
          'X-WP-Nonce': this.nonce,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ [API] Translations fetched successfully for language:', data.lang);

      return data.translations || null;
    } catch (error) {
      console.error('❌ [API] Error fetching translations:', error);
      return null;
    }
  }

  /**
   * Fetch event data including products
   */
  async fetchEventData(): Promise<{ products: Product[]; language?: string } | null> {
    if (!this.eventEndpoint) {
      return null;
    }

    console.log('📅 [API] Fetching event data from WordPress REST API...');

    try {
      const response = await fetch(this.eventEndpoint, {
        headers: {
          'X-WP-Nonce': this.nonce,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ [API] Event data fetched successfully');

      return {
        products: data.event?.products || [],
        language: data.event?.language || '',
      };
    } catch (error) {
      console.error('❌ [API] Error fetching event data:', error);
      return null;
    }
  }

  /**
   * Submit order with offline fallback
   */
  async submitOrderWithOfflineFallback(payload: OrderPayload): Promise<{ success: boolean; message: string; orderId?: string; savedOffline?: boolean }> {
    console.log('🔄 [API] submitOrderWithOfflineFallback called');
    console.log('📡 [API] Browser online status:', navigator.onLine);

    // Check if online
    if (navigator.onLine) {
      console.log('🌐 [API] Browser is online, attempting direct submission...');
      try {
        return await this.submitOrder(payload);
      } catch (error) {
        // If submission fails, save to offline storage
        console.warn('⚠️ [API] Submission failed, saving offline:', error);
        return await this.saveOfflineOrder(payload);
      }
    } else {
      // Save to offline storage
      console.log('📴 [API] Browser is offline, saving to local storage...');
      return await this.saveOfflineOrder(payload);
    }
  }

  /**
   * Save order to offline storage
   */
  private async saveOfflineOrder(payload: OrderPayload): Promise<{ success: boolean; message: string; savedOffline: boolean }> {
    console.log('💾 [API] Saving order to offline storage...');
    try {
      const orderId = await offlineStorage.savePendingOrder(payload);
      console.log('✅ [API] Order saved to offline storage with ID:', orderId);
      return {
        success: true,
        message: 'Order saved locally. Will sync when connection is restored.',
        savedOffline: true,
      };
    } catch (error) {
      console.error('❌ [API] Error saving order offline:', error);
      return {
        success: false,
        message: 'Failed to save order offline. Please check your connection.',
        savedOffline: false,
      };
    }
  }

  /**
   * Sync all pending orders
   */
  async syncPendingOrders(): Promise<{ synced: number; failed: number }> {
    if (this.isSyncingPendingOrders) {
      console.warn('⚠️ [SYNC] Sync already in progress, skipping concurrent call');
      return { synced: 0, failed: 0 };
    }

    this.isSyncingPendingOrders = true;
    try {
      console.log('🔄 [SYNC] Starting sync of pending orders...');
      const pendingOrders = await offlineStorage.getPendingOrders();

      if (pendingOrders.length === 0) {
        console.log('✅ [SYNC] No pending orders to sync');
        return { synced: 0, failed: 0 };
      }

      console.log(`📋 [SYNC] Found ${pendingOrders.length} pending orders to sync`);

      let synced = 0;
      let failed = 0;

      for (const { key: orderId, value: order } of pendingOrders) {
        console.log(`🚀 [SYNC] Syncing order: ${orderId} for ${order.customerInfo.email}`);
        try {
          await this.submitOrder(order);
          await offlineStorage.removePendingOrder(orderId);
          synced++;
          console.log(`✅ [SYNC] Successfully synced order: ${orderId}`);
        } catch (error) {
          console.error(`❌ [SYNC] Failed to sync order ${orderId}:`, error);
          failed++;
        }
      }

      console.log(`📊 [SYNC] Sync complete: ${synced} synced, ${failed} failed`);
      return { synced, failed };
    } finally {
      this.isSyncingPendingOrders = false;
    }
  }

  /**
   * Check if there are pending orders
   */
  async hasPendingOrders(): Promise<boolean> {
    return await offlineStorage.hasPendingOrders();
  }

  /**
   * Get the current language
   */
  getLanguage(): string {
    return this.lang;
  }

  /**
   * Get the event ID
   */
  getEventId(): number | null {
    return this.eventId;
  }
}

// Export singleton instance
let apiServiceInstance: APIService | null = null;

export const getAPIService = (): APIService => {
  if (!apiServiceInstance) {
    apiServiceInstance = new APIService();
  }
  return apiServiceInstance;
};
